import { AppEndpoints } from '../app.endpoints';
import { HttpService } from '../../globals/providers/http.service';
import { Observable } from 'rxjs/Observable';
import {
	ISignInRequest,
	IVerifyCodeRequest,
	ILinkUsersCompleteRequest as ΙFinalizeLinkUserRequest,
	IUserStatusReq,
	ISwitchLinkedUserRequest,
	IUserStatusResponse,
} from '../classes/http-requests';
import {
	ILinkTargetResponse,
	ISendSmsOtpResponse,
	ILinkUsersResponse,
	IInitiateLinkProcessResponse,
} from '../classes/http-responses';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { map, flatMap, take } from 'rxjs/operators';
import { Extensions } from '../classes/static/extensions';
import { UserData, IUserData } from '../../globals/classes/userData';
import { timer } from 'rxjs';

export class LinkUsersService {
	private static _linkUsersResponseSubj: BehaviorSubject<ILinkUsersResponse> =
		new BehaviorSubject<ILinkUsersResponse>(null);
	public static linkUsersResponse$: Observable<ILinkUsersResponse> =
		LinkUsersService._linkUsersResponseSubj.asObservable();

	public static checkUserStatus(username: string, mock = false): Observable<IUserStatusResponse> {
		const url = AppEndpoints.linkUserStatus;
		const request: IUserStatusReq = {
			username: username,
		};
		if (mock == true) {
			return LinkUsersService.checkUserStatusMock(username, url, request);
		} else {
			return LinkUsersService.checkUserStatusActual(username, url, request);
		}
	}

	public static initiateUserLink(
		request: ISignInRequest,
		mock = false
	): Observable<ILinkTargetResponse> {
		const url = AppEndpoints.initiateUserLink;
		if (mock == true) {
			return LinkUsersService.initiateUserLinkMock(url, request);
		} else {
			return LinkUsersService.initiateUserLinkActual(request, url);
		}
	}

	public static verifyTargetOtp(code: string): Observable<ISendSmsOtpResponse> {
		return UserData.data$.pipe(
			take(1),
			flatMap((userData) => {
				const request: IVerifyCodeRequest = { code: code, userId: userData.userId };
				const url = AppEndpoints.verifyTargetOtp;
				return HttpService.post<ISendSmsOtpResponse>(url, request);
			})
		);
	}

	public static verifyInitiatorOtp(code: string): Observable<ILinkUsersResponse> {
		const request: IVerifyCodeRequest = { code: code, userId: null };
		const url = AppEndpoints.verifyInitiatorCode;
		return HttpService.post<ILinkUsersResponse>(url, request).pipe(
			map((response) => {
				LinkUsersService._linkUsersResponseSubj.next(response);
				UserData.update({ userId: response.userId, company: response.company });
				return response;
			})
		);
	}

	public static completeUserLink(): Observable<void> {
		const request: ΙFinalizeLinkUserRequest = {
			returnUrl: Extensions.returnUrl,
			rememberLogin: false,
		};
		const url = AppEndpoints.completeUserLink;
		return HttpService.post(url, request);
	}

	public static initiateUserSwitch(
		request: ISwitchLinkedUserRequest,
		mock = false
	): Observable<IInitiateLinkProcessResponse> {
		const url = AppEndpoints.initiateUserSwitch;
		if (mock == true) {
			return LinkUsersService.initiateUserSwitchMock(url, request);
		} else {
			return LinkUsersService.initiateUserSwitchActual(request, url);
		}
	}

	public static completeUserSwitch(request: ISwitchLinkedUserRequest): Observable<void> {
		const url = AppEndpoints.completeUserSwitch;
		return HttpService.post(url, request);
	}

	public static initiateLinkRenewal(
		userId: string,
		mock = false
	): Observable<IInitiateLinkProcessResponse> {
		const url = AppEndpoints.initiateLineRenewal;
		if (mock == true) {
			return LinkUsersService.initiateLinkRenewalMock(userId, url);
		} else {
			return LinkUsersService.initiateLinkRenewalActual(userId, url);
		}
	}

	private static checkUserStatusMock(
		username: string,
		url: string,
		request: IUserStatusReq
	): Observable<IUserStatusResponse> {
		let dummyResponse: IUserStatusResponse = {
			// changePassword
			userStatus: 'BLOCKED',
			userType: 'COMP_USER',
			ux: null
			// createPassword
			// userStatus: 'NEW',
			// userType: 'COMP_USER',
		};
		let userData: IUserData = {
			hasEmail: false,
			hasSMSOtp: true,
			isChangePass: false,
			userId: 'JENQKA002900008',
			company: 'ΔΗΜΗΤΡΑ ΔΕΤΣΑΡΙΔΟΥ ΖΩΟΤΡΟΦΕΣ ΑΕ',
			userType: 'COMP_USER',
		};
		UserData.update(userData);
		return timer(2500).pipe(map(() => dummyResponse));
	}

	private static checkUserStatusActual(username: string, url: string, request: IUserStatusReq) {
		UserData.update({ userId: username });
		return HttpService.post<IUserStatusResponse>(url, request).pipe(
			map((response) => {
				const userData: IUserData = {
					userId: username,
					userType: !!response.userType ? response.userType.trim() : null,
				};
				UserData.update(userData);
				return response;
			})
		);
	}

	private static initiateUserLinkMock(
		url: string,
		request: ISignInRequest
	): Observable<ILinkTargetResponse> {
		let dummyResponse: ILinkTargetResponse = {
			company: 'ΔΗΜΗΤΡΑ ΔΕΤΣΑΡΙΔΟΥ ΖΩΟΤΡΟΦΕΣ ΑΕ',
			hasOtp: true,
			userId: 'JENQKA002900008',
		};

		return timer(2500).pipe(map(() => dummyResponse));
	}

	private static initiateUserLinkActual(request: ISignInRequest, url: string) {
		UserData.update({ userId: request.username });
		return HttpService.post<ILinkTargetResponse>(url, request).pipe(
			map((response) => {
				UserData.update({
					userId: response.userId,
					company: response.company,
					hasSMSOtp: response.hasOtp,
				});
				return response;
			})
		);
	}

	private static initiateUserSwitchMock(
		url: string,
		request: ISwitchLinkedUserRequest
	): Observable<IInitiateLinkProcessResponse> {
		let dummyResponse: IInitiateLinkProcessResponse = {
			company: 'ΔΗΜΗΤΡΑ ΔΕΤΣΑΡΙΔΟΥ ΖΩΟΤΡΟΦΕΣ ΑΕ',
			hasOtp: true,
			userId: 'JENQKA002900008',
			userType: 'COMP_USER',
		};
		UserData.update(dummyResponse);
		return timer(2500).pipe(map(() => dummyResponse));
	}

	private static initiateUserSwitchActual(request: ISwitchLinkedUserRequest, url: string) {
		UserData.update({ userId: request.userId });
		return HttpService.post<IInitiateLinkProcessResponse>(url, request).pipe(
			map((response) => {
				UserData.update({
					userId: response.userId,
					userType: response.userType,
					hasSMSOtp: response.hasOtp,
					company: response.company,
				});
				return response;
			})
		);
	}

	private static initiateLinkRenewalMock(
		userId: string,
		url: string
	): Observable<IInitiateLinkProcessResponse> {
		let dummyResponse: IInitiateLinkProcessResponse = {
			company: 'ΔΗΜΗΤΡΑ ΔΕΤΣΑΡΙΔΟΥ ΖΩΟΤΡΟΦΕΣ ΑΕ',
			hasOtp: true,
			userId: 'JENQKA002900008',
			userType: 'COMP_USER',
		};
		UserData.update(dummyResponse);
		return timer(2500).pipe(map(() => dummyResponse));
	}

	private static initiateLinkRenewalActual(userId: string, url: string) {
		UserData.update({ userId: userId });
		const request: IUserStatusReq = {
			username: userId,
		};
		return HttpService.post<IInitiateLinkProcessResponse>(url, request).pipe(
			map((response) => {
				UserData.update({
					userId: response.userId,
					userType: response.userType,
					hasSMSOtp: response.hasOtp,
					company: response.company,
				});
				return response;
			})
		);
	}
}
